import {
    DropDown,
    DynamicListAddButton,
    DynamicListWrapperProps,
    requiredRule,
    ValidatedDropDownList,
    ValidatedDynamicList,
    ValidatedDynamicListFormComponentProps,
    ValidatedInput
} from '@finbeat/react-toolkit';
import { ClientBlockReasonAdd } from 'models/api/client';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Col, Row, Table } from 'reactstrap';
import { ClientBlocksFormType } from './ClientBlocksModal';

type ClientBlockProps = {
    index: number;
    actions: DropDown[];
    periods: DropDown[];
    reasons: DropDown[];
}

const ClientBlock = ({ index, actions, periods, reasons }: ClientBlockProps) => {
    const { t } = useTranslation();

    const FormComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) =>
            <ClientBlockReason {...props} idxRoot={index} reasons={reasons} />,
        [reasons]
    );

    return (
        <>
            <Row>
                <Col>
                    <ValidatedDropDownList<ClientBlocksFormType>
                        name={`clientBlocks.${index}.action`}
                        rules={{ required: requiredRule }}
                        disabled={!actions.length}
                        items={actions}
                        label={t('BackOfficeAction')}
                        isClearable={false}
                    />
                </Col>
                <Col>
                    <ValidatedDropDownList<ClientBlocksFormType>
                        name={`clientBlocks.${index}.period`}
                        rules={{ required: requiredRule }}
                        disabled={!periods.length}
                        items={periods}
                        label={t('BackOfficePeriod')}
                        isClearable={false}
                    />
                </Col>
            </Row>
            <Row className="mt-3">
                <Col>
                    <ValidatedDynamicList<ClientBlocksFormType, ClientBlockReasonAdd>
                        formComponent={FormComponent}
                        wrapper={WrapperClientBlockReason}
                        prepend={false}
                        emptyRecord={{
                            name: null
                        }}
                        name={`clientBlocks.${index}.reasons`}
                        compareFunc={(a, b) => a?.name === b?.name}
                    />
                </Col>
            </Row>
            <Row className="mt-3">
                <Col>
                    <ValidatedInput<ClientBlocksFormType>
                        id={`client-block-user-comment-${index}`}
                        name={`clientBlocks.${index}.userComment`}
                        label={t('BackOfficeUserComment')}
                        rules={{
                            required: requiredRule
                        }}
                    />
                </Col>
                <Col>
                    <ValidatedInput<ClientBlocksFormType>
                        id={`client-block-admin-comment-${index}`}
                        name={`clientBlocks.${index}.adminComment`}
                        label={t('BackOfficeAdminComment')}
                        rules={{
                            required: requiredRule
                        }}
                    />
                </Col>
            </Row>
        </>
    );
}

type ClientBlockReasonProps = ValidatedDynamicListFormComponentProps & {
    idxRoot: number;
    reasons: DropDown[];
};

const ClientBlockReason = ({ idxRoot, idx, reasons }: ClientBlockReasonProps) => {
    const { t } = useTranslation();
    return (
        <td>
            <ValidatedDropDownList<ClientBlocksFormType>
                name={`clientBlocks.${idxRoot}.reasons.${idx}.name`}
                rules={{ required: requiredRule }}
                disabled={!reasons.length}
                items={reasons}
                label={t('BackOfficeReason')}
                isClearable={false}
            />
        </td>
    );
};

const WrapperClientBlockReason = ({ children, ...props }: DynamicListWrapperProps) => {
    return (
        <Table bordered>
            <thead>
                <tr className="align-middle">
                    <th>
                        <Trans i18nKey="BackOfficeBlockReasons" />
                    </th>
                    <th className="w-10 text-center">
                        <DynamicListAddButton {...props} />
                    </th>
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </Table>
    );
};

export default ClientBlock;
